import { useEffect, useMemo, useRef, useState } from 'react'

import { useLazyQuery } from '@apollo/client'
import {
  Alert,
  Checkbox,
  Divider,
  Group,
  Loader,
  Modal,
  ScrollArea,
  Select,
  Switch,
  Table,
  Text,
} from '@mantine/core'
import { DateInput } from '@mantine/dates'

import { useMutation, useQuery } from '@redwoodjs/web'

import Button from 'src/components/Buttons/Button/Button'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_AUTOMATED_RULES } from 'src/graphql/automatedRules.graphql'
import {
  PREVIEW_BULK_SCHEDULE_AUTOMATIONS,
  SCHEDULE_BULK_AUTOMATIONS,
} from 'src/graphql/bulkScheduleAutomations.graphql'
import { AutomatedRuleStatus } from 'src/graphql/types/automatedRules'
import { formatSnakeValue } from 'src/lib/formatters'
import {
  bucketRulesByStage,
  MAIN_STAGE_KEYS,
  OUTCOME_STAGE_KEYS,
  type StageKey,
} from 'src/pages/SettingsPage/tabs/RulesTab/utils/pipelineBucketing'

// Rules query paginates; ask for a generous ceiling so every active rule is
// selectable from a single fetch (companies have well under this many rules).
const RULES_PAGE_LIMIT = 500

type LookbackPreset = '1y' | '2y' | 'custom'

type RuleCondition = {
  key: string
  value?: string | null
}

type Rule = {
  automatedRuleId: string
  name: string
  status: string
  conditions: RuleCondition[]
  frequency?: string | null
  triggerDelay?: number | null
}

type RuleCounts = {
  automatedRuleId: string
  supported: boolean
  eligible: number
  willSchedule: number
  pastWindow: number
  alreadyScheduled: number
  alreadyCompleted: number
}

type Preview = {
  perRule: RuleCounts[]
  aggregate: {
    eligible: number
    willSchedule: number
    pastWindow: number
    alreadyScheduled: number
    alreadyCompleted: number
  }
  cap: number
  overCap: boolean
}

// Stage groups render top row (in-progress) then outcome lanes, mirroring the
// pipeline view. UNSCOPED rules (no stage anchor) land in their own bucket.
const STAGE_ORDER: StageKey[] = [
  ...MAIN_STAGE_KEYS,
  ...OUTCOME_STAGE_KEYS,
  'UNSCOPED',
]

const stageLabel = (key: StageKey): string =>
  key === 'UNSCOPED' ? 'Other / unscoped' : formatSnakeValue(key)

const yearsAgo = (years: number): Date => {
  const date = new Date()
  date.setFullYear(date.getFullYear() - years)
  return date
}

type ScheduleAutomationsModalProps = {
  opened: boolean
  onClose: () => void
}

const ScheduleAutomationsModal = ({
  opened,
  onClose,
}: ScheduleAutomationsModalProps) => {
  const [selectedRuleIds, setSelectedRuleIds] = useState<Set<string>>(new Set())
  const [lookback, setLookback] = useState<LookbackPreset>('1y')
  const [customStart, setCustomStart] = useState<Date | null>(yearsAgo(1))
  const [eligibleForRehireOnly, setEligibleForRehireOnly] = useState(false)
  const [preview, setPreview] = useState<Preview | null>(null)

  const { data: rulesData, loading: rulesLoading } = useQueryActiveRules(opened)

  const rules: Rule[] = useMemo(
    () => rulesData?.automatedRules?.items ?? [],
    [rulesData]
  )

  const ruleNameById = useMemo(() => {
    const map = new Map<string, string>()
    rules.forEach((rule) => map.set(rule.automatedRuleId, rule.name))
    return map
  }, [rules])

  const groupedRules = useMemo(() => bucketRulesByStage(rules), [rules])

  const windowStart = useMemo(() => {
    if (lookback === '1y') return yearsAgo(1)
    if (lookback === '2y') return yearsAgo(2)
    return customStart
  }, [lookback, customStart])

  // Any change to the selection/window bumps this counter; a preview result is
  // only applied if the counter still matches what it was when the request was
  // sent (see handleRunPreview + onCompleted). Stops an in-flight preview from
  // reviving a result for a selection the operator has since changed — which
  // would otherwise let Schedule dispatch a different selection than the table
  // shows.
  const changeCounterRef = useRef(0)
  const requestedChangeRef = useRef(-1)
  useEffect(() => {
    changeCounterRef.current += 1
  }, [selectedRuleIds, windowStart, eligibleForRehireOnly])

  const [runPreview, { loading: previewLoading }] = useLazyQuery(
    PREVIEW_BULK_SCHEDULE_AUTOMATIONS,
    {
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        // Discard a result whose selection/window has since changed.
        if (requestedChangeRef.current !== changeCounterRef.current) {
          return
        }
        setPreview(data?.previewBulkScheduleAutomations ?? null)
      },
      onError: () => {
        // Ignore a late failure from a request whose selection/window has since
        // changed — it must not wipe a newer valid preview.
        if (requestedChangeRef.current !== changeCounterRef.current) {
          return
        }
        setPreview(null)
        toast('Unable to preview bulk scheduling, please try again.', 'error')
      },
    }
  )

  const [runSchedule, { loading: scheduleLoading }] = useMutation(
    SCHEDULE_BULK_AUTOMATIONS,
    {
      onCompleted: (data) => {
        const willSchedule =
          data?.scheduleBulkAutomations?.aggregate?.willSchedule ?? 0
        toast(`Scheduling started for ${willSchedule} applicants`, 'success')
        handleClose()
      },
      onError: () => {
        toast('Unable to start bulk scheduling, please try again.', 'error')
      },
    }
  )

  const selectedRuleIdList = useMemo(
    () => Array.from(selectedRuleIds),
    [selectedRuleIds]
  )

  const buildVariables = () => ({
    ruleIds: selectedRuleIdList,
    targeting: {
      windowStart,
      // windowEnd omitted — backend defaults it to now.
      ...(eligibleForRehireOnly && { eligibleForRehire: true }),
    },
  })

  const handleRunPreview = () => {
    // Snapshot the current change counter so onCompleted can tell whether the
    // selection/window changed while this preview was in flight.
    requestedChangeRef.current = changeCounterRef.current
    runPreview({ variables: buildVariables() })
  }

  const toggleRule = (ruleId: string) => {
    setPreview(null)
    setSelectedRuleIds((prev) => {
      const next = new Set(prev)
      if (next.has(ruleId)) {
        next.delete(ruleId)
      } else {
        next.add(ruleId)
      }
      return next
    })
  }

  const toggleGroup = (stageRules: Rule[], allSelected: boolean) => {
    setPreview(null)
    setSelectedRuleIds((prev) => {
      const next = new Set(prev)
      stageRules.forEach((rule) => {
        if (allSelected) {
          next.delete(rule.automatedRuleId)
        } else {
          next.add(rule.automatedRuleId)
        }
      })
      return next
    })
  }

  const handleClose = () => {
    setSelectedRuleIds(new Set())
    setLookback('1y')
    setCustomStart(yearsAgo(1))
    setEligibleForRehireOnly(false)
    setPreview(null)
    onClose()
  }

  const canPreview =
    selectedRuleIdList.length > 0 && !!windowStart && !previewLoading

  const canSchedule =
    !!preview &&
    !preview.overCap &&
    preview.aggregate.willSchedule > 0 &&
    !scheduleLoading

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Schedule Automations (Internal)"
      size="xl"
      closeOnClickOutside={!scheduleLoading}
      closeOnEscape={!scheduleLoading}
      classNames={{
        title: 'text-base font-semibold text-doubleNickel-gray-900',
      }}
    >
      <div className="flex flex-col gap-4">
        <Alert color="orange" variant="light">
          <Text size="xs">
            Internal tool. Bulk-schedules the selected active automations for
            this company&apos;s applicants based on each rule&apos;s historical
            stage date. Run a preview before scheduling.
          </Text>
        </Alert>

        {/* Rules multi-select, grouped by stage */}
        <div className="flex flex-col gap-2">
          <Text size="xs" fw={600} className="text-doubleNickel-gray-500">
            AUTOMATED RULES ({selectedRuleIdList.length} selected)
          </Text>
          {rulesLoading ? (
            <Group>
              <Loader size="xs" />
              <Text size="xs">Loading active rules…</Text>
            </Group>
          ) : rules.length === 0 ? (
            <Text size="xs" className="text-doubleNickel-gray-500">
              No active automated rules for this company.
            </Text>
          ) : (
            <ScrollArea.Autosize mah={260} type="auto">
              <div className="flex flex-col gap-3 pr-2">
                {STAGE_ORDER.map((stageKey) => {
                  const stageRules = groupedRules[stageKey]
                  if (!stageRules || stageRules.length === 0) return null

                  const selectedCount = stageRules.filter((rule) =>
                    selectedRuleIds.has(rule.automatedRuleId)
                  ).length
                  const allSelected = selectedCount === stageRules.length
                  const someSelected = selectedCount > 0 && !allSelected

                  return (
                    <div key={stageKey} className="flex flex-col gap-1">
                      <Checkbox
                        size="xs"
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={() => toggleGroup(stageRules, allSelected)}
                        label={
                          <Text size="xs" fw={600}>
                            {stageLabel(stageKey)} ({stageRules.length})
                          </Text>
                        }
                      />
                      <div className="ml-6 flex flex-col gap-1">
                        {stageRules.map((rule) => (
                          <Checkbox
                            key={rule.automatedRuleId}
                            size="xs"
                            checked={selectedRuleIds.has(rule.automatedRuleId)}
                            onChange={() => toggleRule(rule.automatedRuleId)}
                            label={<Text size="xs">{rule.name}</Text>}
                          />
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            </ScrollArea.Autosize>
          )}
        </div>

        <Divider variant="dashed" />

        {/* Window control */}
        <div className="flex flex-row items-center justify-between">
          <Text size="xs">Look back window</Text>
          <div className="w-[250px]">
            <Select
              size="xs"
              value={lookback}
              onChange={(value) => {
                setPreview(null)
                setLookback((value as LookbackPreset) ?? '1y')
              }}
              data={[
                { value: '1y', label: 'Past 1 year' },
                { value: '2y', label: 'Past 2 years' },
                { value: 'custom', label: 'Custom start date' },
              ]}
              allowDeselect={false}
              classNames={{ input: 'text-xs text-right' }}
            />
          </div>
        </div>

        {lookback === 'custom' && (
          <div className="flex flex-row items-center justify-between">
            <Text size="xs">Start date</Text>
            <div className="w-[250px]">
              <DateInput
                size="xs"
                value={customStart}
                onChange={(value) => {
                  setPreview(null)
                  setCustomStart(value)
                }}
                maxDate={new Date()}
                valueFormat="MM/DD/YYYY"
                placeholder="Select start date"
                classNames={{ input: 'text-xs text-right' }}
              />
            </div>
          </div>
        )}

        <div className="flex flex-row items-center justify-between">
          <Text size="xs">Eligible for rehire only</Text>
          <Switch
            size="sm"
            checked={eligibleForRehireOnly}
            onChange={(event) => {
              setPreview(null)
              setEligibleForRehireOnly(event.currentTarget.checked)
            }}
          />
        </div>

        <Group>
          <Button
            text="Preview"
            variant="outline"
            disabled={!canPreview}
            loading={previewLoading}
            onClick={handleRunPreview}
          />
        </Group>

        {/* Preview results */}
        {preview && (
          <div className="flex flex-col gap-3">
            <Divider variant="dashed" />

            {preview.overCap && (
              <Alert color="red" variant="light">
                <Text size="xs">
                  Would schedule {preview.aggregate.willSchedule} applicants,
                  over the limit of {preview.cap}. Narrow the window or select
                  fewer rules.
                </Text>
              </Alert>
            )}

            <Table
              horizontalSpacing="xs"
              verticalSpacing="xs"
              className="text-xs"
            >
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Rule</Table.Th>
                  <Table.Th>Eligible</Table.Th>
                  <Table.Th>Will schedule</Table.Th>
                  <Table.Th>Past window</Table.Th>
                  <Table.Th>Already scheduled</Table.Th>
                  <Table.Th>Already completed</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {preview.perRule.map((row) => (
                  <Table.Tr key={row.automatedRuleId}>
                    <Table.Td>
                      <Text size="xs">
                        {ruleNameById.get(row.automatedRuleId) ??
                          row.automatedRuleId}
                      </Text>
                      {!row.supported && (
                        <Text size="xs" c="dimmed">
                          Not supported for bulk scheduling
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>{row.eligible}</Table.Td>
                    <Table.Td>{row.willSchedule}</Table.Td>
                    <Table.Td>{row.pastWindow}</Table.Td>
                    <Table.Td>{row.alreadyScheduled}</Table.Td>
                    <Table.Td>{row.alreadyCompleted}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
              <Table.Tfoot>
                <Table.Tr className="font-semibold">
                  <Table.Td>
                    <Text size="xs" fw={600}>
                      Total
                    </Text>
                  </Table.Td>
                  <Table.Td>{preview.aggregate.eligible}</Table.Td>
                  <Table.Td>{preview.aggregate.willSchedule}</Table.Td>
                  <Table.Td>{preview.aggregate.pastWindow}</Table.Td>
                  <Table.Td>{preview.aggregate.alreadyScheduled}</Table.Td>
                  <Table.Td>{preview.aggregate.alreadyCompleted}</Table.Td>
                </Table.Tr>
              </Table.Tfoot>
            </Table>

            {preview.aggregate.willSchedule === 0 && !preview.overCap && (
              <Text size="xs" c="dimmed">
                Nothing to schedule for the current selection and window.
              </Text>
            )}
          </div>
        )}

        <Group className="mt-2 grid grid-cols-2">
          <Button
            text="Cancel"
            variant="outline"
            onClick={handleClose}
            disabled={scheduleLoading}
          />
          <Button
            text="Schedule"
            variant="filled"
            disabled={!canSchedule}
            loading={scheduleLoading}
            onClick={() => runSchedule({ variables: buildVariables() })}
          />
        </Group>
      </div>
    </Modal>
  )
}

// Fetch active rules only, and only while the modal is open, so the list stays
// scoped to what can actually be bulk-scheduled.
const useQueryActiveRules = (opened: boolean) => {
  return useQuery(GET_AUTOMATED_RULES, {
    skip: !opened,
    variables: {
      pageInfo: { offset: 0, limit: RULES_PAGE_LIMIT },
      filters: { statuses: [AutomatedRuleStatus.ACTIVE] },
    },
  })
}

export default ScheduleAutomationsModal
